/* Memory Recall — Per-Bot Storage and Summary Helpers */
/* Mirrors languageMode.ts pattern — localStorage per botId */
/* Delivery: summary travels via the session.connect URL metadata param ONLY. */
/* The ibot_memory_recall cookie was removed — the relay never read it. */
/* (C) 2024 - 2026, by Drew Anderson - Cloud Media Works, LLC - All Rights Reserved */

import { HistoryItem, getMemoryRecallSessions } from '../utils/botFactoryAPI';

// Per-bot localStorage key — scoped to botId, never shared across bots
function memoryRecallStorageKey(botId: string): string {
  return `ibot_memory_recall_summary_${botId}`;
}

// Summary character cap — summary travels via session.connect URL metadata (not the
// cookie, which carries botId only), so the cap guards URL length, not the 4KB cookie limit.
const SUMMARY_MAX_CHARS = 1000;

export function setMemoryRecallSummary(botId: string, summary: string): void {
  const capped = summary.slice(0, SUMMARY_MAX_CHARS);

  try {
    localStorage.setItem(memoryRecallStorageKey(botId), capped);
  } catch (e) {
    console.warn('[MemoryRecall] localStorage write failed:', e);
  }
}

export function getMemoryRecallSummary(botId: string): string | null {
  try {
    return localStorage.getItem(memoryRecallStorageKey(botId)) || null;
  } catch {
    return null;
  }
}

export function clearMemoryRecallSummary(botId: string): void {
  try {
    localStorage.removeItem(memoryRecallStorageKey(botId));
  } catch {
    // ignore
  }
}

// Returns the memory text for inclusion in the session.connect metadata payload:
// compact summary (Start Message recap) + [SESSION DETAILS] turn-by-turn section
// (recall commands / "show session N"). Composed fresh from the rolling cache when
// available; falls back to the stored summary string (authenticated API path writes
// only that key). Returns null if nothing is stored (first session or cleared).
export function buildMemoryMetadataPayload(botId: string): string | null {
  const entries = readRecallSessions(botId);
  if (entries.length > 0) {
    const summary = composeRecallSummary(entries);
    const details = composeRecallDetails(entries);
    const combined = [summary, details].filter(Boolean).join('\n\n');
    return combined || null;
  }
  return getMemoryRecallSummary(botId);
}

// Generate a concise algorithmic summary from HistoryItem[].
// Groups messages by runId (session), extracts the first SUBSTANTIVE exchange per
// session — skipping the first assistant transcript, which is always the SCB Start
// Message greeting and carries no conversation content.
// Formats as a dated bullet list. Returns empty string if no usable items.
export function generateMemorySummary(items: HistoryItem[]): string {
  if (!items || items.length === 0) return '';

  // Group by runId — each unique runId is one session
  const sessionItems = new Map<string, HistoryItem[]>();
  for (const item of items) {
    const key = item.runId || 'unknown';
    if (!sessionItems.has(key)) sessionItems.set(key, []);
    sessionItems.get(key)!.push(item);
  }

  const sessionMap = new Map<string, { time: string; snippet: string; lastTopic: string }>();

  sessionItems.forEach((entries, key) => {
    // Sort entries oldest-first so "first" means chronological start of the session
    const ordered = [...entries].sort((a, b) => {
      const ta = a.time ? new Date(a.time).getTime() : 0;
      const tb = b.time ? new Date(b.time).getTime() : 0;
      return ta - tb;
    });

    const assistants = ordered.filter((e) => e.role === 'assistant' && e.transcript);
    const users = ordered.filter((e) => e.role === 'user' && e.transcript);

    // Skip assistants[0] — the Start Message greeting. Prefer the first substantive
    // assistant reply; fall back to the first user message. Greeting-only sessions
    // produce no entry.
    const source = assistants[1] || users[0] || null;
    if (!source) return;

    // Capture BOTH sides: up to 3 user topics plus the first substantive agent reply.
    const cleanText = (t: string) => t.trim().replace(/\s+/g, ' ');
    const topics = users
      .slice(0, 3)
      .map((u) => `"${cleanText(u.transcript!).slice(0, 50)}"`)
      .join(', ');
    const lastTopic = users.length
      ? cleanText(users[users.length - 1].transcript!).slice(0, 50)
      : '';
    const replySnippet = cleanText(source.transcript!).slice(0, 100);
    const snippet =
      topics && source !== users[0]
        ? `User asked about: ${topics} — ${replySnippet}`
        : replySnippet;

    sessionMap.set(key, {
      time: ordered[0]?.time || '',
      snippet,
      lastTopic,
    });
  });

  if (sessionMap.size === 0) return '';

  // Sort sessions newest first by time
  const sessions = Array.from(sessionMap.entries()).sort((a, b) => {
    const ta = a[1].time ? new Date(a[1].time).getTime() : 0;
    const tb = b[1].time ? new Date(b[1].time).getTime() : 0;
    return tb - ta;
  });

  const mostRecent = sessions[0]?.[1]?.time
    ? new Date(sessions[0][1].time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const bullets = sessions
    .map(([, s]) => {
      const dateStr = s.time
        ? new Date(s.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
      return `• ${dateStr ? `[${dateStr}] ` : ''}${s.snippet}`;
    })
    .join('\n');

  // Last topic of the most recent session — used by the SCB to offer continuation.
  const lastTopic = sessions[0]?.[1]?.lastTopic || '';

  const lines = [
    'Recent conversations:',
    bullets,
    mostRecent ? `Most recent session: ${mostRecent}.` : '',
    lastTopic ? `Last topic: "${lastTopic}".` : '',
  ]
    .filter(Boolean)
    .join('\n');

  return lines.slice(0, SUMMARY_MAX_CHARS);
}

// ─── Single canonical startup hook ─────────────────────────────────────────
// Called once per page load for any bot with MemoryRecall enabled (iBot.json flag).
// Fetches prior sessions from Robot Factory and refreshes the stored summary.
// On failure or empty results, the existing localStorage cache (rolling sessions +
// summary key) is used as-is by buildMemoryMetadataPayload — no action needed here.
// NOTE: the global 'ibot.memoryRecall.activeBot' slot was REMOVED — it was shared across
// bots and tabs and caused cross-bot memory bleed. Pages track their own recall bot in a
// per-page ref (memoryRecallBotIdRef).
export async function startupMemoryRecall(
  botId: string,
  userId: string,
  runId: string | null,
): Promise<void> {
  try {
    const recallToken =
      document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null) ||
      '';
    const recallItems = await getMemoryRecallSessions(userId, botId, recallToken, runId);
    if (recallItems && recallItems.length > 0) {
      const summary = generateMemorySummary(recallItems);
      if (summary) {
        setMemoryRecallSummary(botId, summary);
      }
    }
  } catch (err: any) {
    console.warn('[MemoryRecall] Startup fetch failed — using localStorage cache:', err?.message);
  }
}
// ───────────────────────────────────────────────────────────────────────────

// ─── Rolling per-bot session cache (guest mode primary store) ──────────────
// Spec v1.01: recall covers the last 30 days, max 6 sessions. Guests have no
// server-side history (guest userIds rotate per sign-in and transcripts are not
// POSTed), so this localStorage array IS the guest history store.

export interface RecallTurn {
  u: string; // user turn text (capped)
  a: string; // following agent reply snippet (capped)
}

export interface RecallSessionEntry {
  time: string; // ISO timestamp of the session
  snippet: string; // both-sides summary of the session
  lastTopic: string; // last user topic — used for the continuation offer
  turns?: RecallTurn[]; // fuller both-sides exchanges — feeds [SESSION DETAILS]
}

const RECALL_SESSIONS_KEY = (botId: string) => `ibot_memory_recall_sessions_${botId}`;
const MAX_RECALL_SESSIONS = 6;
const MAX_RECALL_AGE_DAYS = 30;
const MAX_TURNS_PER_SESSION = 10;
const USER_TURN_MAX_CHARS = 80;
const AGENT_TURN_MAX_CHARS = 160;
// Separate cap for the [SESSION DETAILS] section appended after the compact summary.
// Total relay payload ≈ SUMMARY_MAX_CHARS + RECALL_DETAILS_MAX_CHARS — kept well under
// typical 16KB URL/header limits after encodeURIComponent inflation.
const RECALL_DETAILS_MAX_CHARS = 3000;

// Read + age-filter the rolling session cache for a bot. Never throws.
function readRecallSessions(botId: string): RecallSessionEntry[] {
  let entries: RecallSessionEntry[] = [];
  try {
    entries = JSON.parse(localStorage.getItem(RECALL_SESSIONS_KEY(botId)) || '[]');
    if (!Array.isArray(entries)) entries = [];
  } catch {
    entries = [];
  }
  const cutoff = Date.now() - MAX_RECALL_AGE_DAYS * 24 * 60 * 60 * 1000;
  return entries.filter(
    (e) => e && e.time && e.snippet && new Date(e.time).getTime() >= cutoff
  );
}

// Compose the relay-bound summary text from cached entries (newest first).
// Header lines come BEFORE the bullets so SUMMARY_MAX_CHARS truncation drops the
// OLDEST sessions, never the "Most recent session" / "Last topic" lines.
function composeRecallSummary(entries: RecallSessionEntry[]): string {
  if (!entries || entries.length === 0) return '';
  const mostRecent = entries[0]?.time
    ? new Date(entries[0].time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const lastTopic = entries[0]?.lastTopic || '';
  const bullets = entries
    .map((e) => {
      const dateStr = e.time
        ? new Date(e.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
      return `• ${dateStr ? `[${dateStr}] ` : ''}${e.snippet}`;
    })
    .join('\n');
  return [
    'Recent conversations:',
    mostRecent ? `Most recent session: ${mostRecent}.` : '',
    lastTopic ? `Last topic: "${lastTopic}".` : '',
    bullets,
  ]
    .filter(Boolean)
    .join('\n')
    .slice(0, SUMMARY_MAX_CHARS);
}

// Compose the turn-by-turn [SESSION DETAILS] section from cached entries (newest first).
// Sessions are numbered to match the summary bullets; whole sessions are dropped from the
// OLDEST end when the cap is reached, so Session 1 (most recent) is always complete.
function composeRecallDetails(entries: RecallSessionEntry[]): string {
  const withTurns = entries.filter((e) => e.turns && e.turns.length > 0);
  if (withTurns.length === 0) return '';

  const footer = '[END SESSION DETAILS]';
  let out = '[SESSION DETAILS — most recent first]';
  for (let i = 0; i < withTurns.length; i++) {
    const e = withTurns[i];
    const dateStr = e.time
      ? new Date(e.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'unknown date';
    const turnLines = (e.turns || [])
      .slice(0, MAX_TURNS_PER_SESSION)
      .map((t) => `  - User: "${t.u}"${t.a ? ` → Agent: ${t.a}` : ''}`)
      .join('\n');
    const section = `\nSession ${i + 1} — ${dateStr}:\n${turnLines}`;
    if (out.length + section.length + footer.length + 1 > RECALL_DETAILS_MAX_CHARS) break;
    out += section;
  }
  return out + '\n' + footer;
}

// Dedupe guard: beforeunload, pagehide, and unload can ALL fire on one navigation.
// The old overwrite was idempotent; an append is not — save at most once per page load.
let _sessionEntrySaved = false;

// Append the current session to the rolling cache (newest first, 30-day window,
// max 6 entries), then recompose and store the relay-bound summary text.
export function appendMemoryRecallSession(botId: string, entry: RecallSessionEntry): void {
  if (_sessionEntrySaved) return;
  _sessionEntrySaved = true;

  let entries = readRecallSessions(botId);
  entries.unshift(entry);
  entries = entries.slice(0, MAX_RECALL_SESSIONS);

  try {
    localStorage.setItem(RECALL_SESSIONS_KEY(botId), JSON.stringify(entries));
  } catch (e) {
    console.warn('[MemoryRecall] Session cache write failed:', e);
  }

  const summary = composeRecallSummary(entries);
  if (summary) {
    setMemoryRecallSummary(botId, summary);
  }
}
// ───────────────────────────────────────────────────────────────────────────

// Build a RecallSessionEntry from the current session's conversation items.
// Used in beforeunload handlers where only the live RealtimeClient item format is available.
// Items are expected to have shape: { role, formatted: { transcript?, text? } }
// Returns null for greeting-only sessions (nothing worth remembering).
export function generateCurrentSessionEntry(items: any[]): RecallSessionEntry | null {
  if (!items || items.length === 0) return null;

  const assistantItems = items.filter(
    (item: any) =>
      item?.role === 'assistant' &&
      ((item?.formatted?.transcript || '').trim() || (item?.formatted?.text || '').trim())
  );

  if (assistantItems.length === 0) return null;

  const userItems = items.filter(
    (item: any) =>
      item?.role === 'user' &&
      ((item?.formatted?.transcript || '').trim() || (item?.formatted?.text || '').trim())
  );

  // Skip assistantItems[0] — it is always the SCB Start Message greeting and carries
  // no conversation content. Require a substantive assistant reply or a user message;
  // greeting-only sessions return null and are not saved as memory.
  const sourceItem = assistantItems[1] || null;
  if (!sourceItem && userItems.length === 0) return null;

  const getText = (item: any): string =>
    ((item?.formatted?.transcript || item?.formatted?.text || '') as string)
      .trim()
      .replace(/\s+/g, ' ');

  // Capture BOTH sides: up to 3 user topics plus the first substantive agent reply.
  const topics = userItems
    .slice(0, 3)
    .map((u: any) => `"${getText(u).slice(0, 50)}"`)
    .join(', ');
  const lastTopic = userItems.length
    ? getText(userItems[userItems.length - 1]).slice(0, 50)
    : '';
  const replySnippet = sourceItem ? getText(sourceItem).slice(0, 100) : '';

  const snippet = [topics ? `User asked about: ${topics}` : '', replySnippet]
    .filter(Boolean)
    .join(' — ');

  if (!snippet) return null;

  // Fuller both-sides record: every user turn paired with the agent reply that follows it.
  // Feeds the [SESSION DETAILS] section used by the SCB recall commands ("show session N").
  const turns: RecallTurn[] = [];
  for (let i = 0; i < items.length && turns.length < MAX_TURNS_PER_SESSION; i++) {
    const it = items[i];
    if (it?.role !== 'user') continue;
    const u = getText(it).slice(0, USER_TURN_MAX_CHARS);
    if (!u) continue;
    let a = '';
    for (let j = i + 1; j < items.length; j++) {
      if (items[j]?.role === 'assistant') {
        a = getText(items[j]).slice(0, AGENT_TURN_MAX_CHARS);
        break;
      }
      if (items[j]?.role === 'user') break; // next turn started without a reply
    }
    turns.push({ u, a });
  }

  return {
    time: new Date().toISOString(),
    snippet,
    lastTopic,
    turns,
  };
}
